import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Grid,
  GridItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { InputField, SwitchField } from 'app/components/Core';
import { useIpiDialogSubmit } from 'app/components/Profile/Ids/IpiDialog/hooks';
import { getPlayerHeight } from 'app/store/AudioPlayer/selectors';
import { useAppSelector } from 'app/store';
import { SocietySelect } from '../SocietySelect';
import { toSocietyOptions } from '../utils';
import { IpiDialogComponentProps, IpiForm } from './types';
import {
  getAddIpiValidationSchema,
  getDefaultValues,
  getUpdateIpiValidationSchema,
} from './utils';

export const IpiDialogComponent: React.FC<IpiDialogComponentProps> = ({
  open,
  societies,
  ipiValidations,
  ipi,
  areSocietiesLoading,
  updateIpi,
  handleAddIpiV0,
  handleAddIpiV2,
  handleAddIpiV3,
  onClose,
}) => {
  const playerHeight = useAppSelector(getPlayerHeight);
  const {
    control,
    formState: { isValid, isSubmitting, isDirty, dirtyFields, errors },
    handleSubmit,
    reset,
    watch,
    setValue,
    trigger,
    register,
  } = useForm<IpiForm>({
    defaultValues: getDefaultValues(ipi),
    mode: 'all',
    resolver: yupResolver(
      ipi
        ? getUpdateIpiValidationSchema(ipiValidations)
        : getAddIpiValidationSchema(ipiValidations)
    ),
  });
  const handleClose = (status?: number) => {
    onClose(status);
    reset();
  };

  const { onSubmit } = useIpiDialogSubmit({
    handleSubmit,
    ipi,
    dirtyFields,
    handleAddIpiV3,
    handleAddIpiV2,
    handleAddIpiV0,
    updateIpi,
    handleClose,
  });
  const watchSociety = watch('society');

  useEffect(() => {
    if (watchSociety?.verification || !watchSociety) {
      setValue('ipiNameNumber', '');
    }
  }, [setValue, watchSociety]);

  useEffect(() => {
    if (open) {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const showDetailsFields = watchSociety && !watchSociety?.verification;

  return (
    <Modal isOpen={open} isCentered onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bottom={`${playerHeight}px`}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            return onSubmit(e);
          }}
          data-testid="ipi-form"
        >
          <ModalHeader
            data-testid="dialog-title"
            title={`${ipi ? 'Edit' : 'Add'} IPI Number`}
          >
            {ipi ? 'Edit' : 'Add'} IPI Number
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <Grid
              columnGap={4}
              templateRows={`repeat(${showDetailsFields ? 3 : 1}, 1fr)`}
              templateColumns="repeat(2, 1fr)"
            >
              <GridItem colSpan={2}>
                <SocietySelect
                  name="society"
                  value={watchSociety}
                  control={control}
                  options={toSocietyOptions(societies)}
                  isDisabled={Boolean(ipi)}
                  loading={areSocietiesLoading}
                />
              </GridItem>
              {showDetailsFields && (
                <>
                  <GridItem>
                    <InputField
                      label="First Name"
                      {...register('firstName', {
                        onChange: () => trigger('lastName'),
                        onBlur: () => trigger('lastName'),
                      })}
                      isInvalid={Boolean(errors.firstName)}
                      errorMessage={errors.firstName?.message}
                    />
                  </GridItem>
                  <GridItem>
                    <InputField
                      label="Last Name"
                      {...register('lastName', {
                        onChange: () => trigger('firstName'),
                        onBlur: () => trigger('firstName'),
                      })}
                      isInvalid={Boolean(errors.lastName)}
                      errorMessage={errors.lastName?.message}
                    />
                  </GridItem>
                  <GridItem>
                    <InputField
                      label="IPI Number"
                      {...register('ipiNameNumber')}
                      isDisabled={Boolean(ipi)}
                      isInvalid={Boolean(errors.ipiNameNumber)}
                      errorMessage={errors.ipiNameNumber?.message}
                    />
                  </GridItem>
                  <GridItem>
                    <SwitchField
                      label="Use as Default IPI"
                      labelProps={{
                        htmlFor: 'isDefault',
                      }}
                      data-testid="default-label"
                      {...register('isDefault')}
                    />
                  </GridItem>
                </>
              )}
            </Grid>
          </ModalBody>
          <ModalFooter data-testid="dialog-actions">
            <Button
              onClick={() => handleClose()}
              variant="secondary"
              data-testid="IpiDialogComponent-cancel"
            >
              Cancel
            </Button>
            <Button
              ml={2}
              type="submit"
              isDisabled={
                !isValid || isSubmitting || (Boolean(ipi) && !isDirty)
              }
              data-testid="IpiDialogComponent-submit-btn"
            >
              {ipi ? 'Save' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
